/**
 * SPEC-RedDotOps — shapes and validation shared by the server and the Admin
 * "Red dot responders" card.
 *
 * The roster is one global `app_settings` row (key `red_dot_roster`): the
 * people who get tagged when someone sends a Bad delivery to the ops channel.
 */

export interface RedDotResponder {
  name: string;
  slackUserId: string;
  active: boolean;
}

export interface RedDotRoster {
  responders: RedDotResponder[];
}

export const EMPTY_RED_DOT_ROSTER: RedDotRoster = { responders: [] };

export const RED_DOT_MAX_RESPONDERS = 20;
export const RED_DOT_NAME_MAX = 40;
/** Slack member IDs start with U (or W on Enterprise Grid). */
export const SLACK_USER_ID_RE = /^[UW][A-Z0-9]{6,}$/;

/** The free-text message is capped before it is posted (§3). */
export const RED_DOT_MESSAGE_MAX = 500;

/**
 * `initial_action` has a DB CHECK IN ('phone','text','other'), so the #ops chip
 * ("slack" on the API) is STORED as 'other' with this text (§6). The
 * `red_dot_sent` audit row is the source of truth for the send itself.
 */
export const RED_DOT_STORED_ACTION_OTHER = "Sent to Ops (Slack)";

export type RedDotRosterValidation =
  | { ok: true; roster: RedDotRoster }
  | { ok: false; error: string };

/** Validate (and trim) a roster. Used by the PUT endpoint and the Admin card. */
export function validateRedDotRoster(input: unknown): RedDotRosterValidation {
  const responders = (input as { responders?: unknown } | null)?.responders;
  if (!Array.isArray(responders)) return { ok: false, error: "responders must be a list" };
  if (responders.length > RED_DOT_MAX_RESPONDERS) {
    return { ok: false, error: `At most ${RED_DOT_MAX_RESPONDERS} people` };
  }

  const seen = new Set<string>();
  const clean: RedDotResponder[] = [];
  for (const raw of responders) {
    const r = raw as Partial<RedDotResponder> | null;
    const name = typeof r?.name === "string" ? r.name.trim() : "";
    const slackUserId = typeof r?.slackUserId === "string" ? r.slackUserId.trim() : "";
    if (name.length < 1 || name.length > RED_DOT_NAME_MAX) {
      return { ok: false, error: `Name must be 1–${RED_DOT_NAME_MAX} characters` };
    }
    if (!SLACK_USER_ID_RE.test(slackUserId)) {
      return { ok: false, error: `"${slackUserId}" is not a Slack member ID (it looks like U0123ABC)` };
    }
    if (seen.has(slackUserId)) {
      return { ok: false, error: `${slackUserId} is already on the list` };
    }
    if (typeof r?.active !== "boolean") return { ok: false, error: "active must be true or false" };
    seen.add(slackUserId);
    clean.push({ name, slackUserId, active: r.active });
  }
  return { ok: true, roster: { responders: clean } };
}

/** The `redDot` field on the /decision and /red-dot responses (§5). */
export interface RedDotResult {
  requested: boolean;
  ok: boolean;
  error?: string;
}
